var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { builder, Builder, withChildren } from '@builder.io/react';
import { withBlockContext } from './BlockContext';
import { accordionConfig, accordionSectionConfig, anchorConfig, articleConfig, blockConfig, blockV2Config, blockGridConfig, brandButtonConfig, breadcrumbsConfig, buttonConfig, buttonNavSectionConfig, carouselSectionConfig, chatBotConfig, comparisonTableConfig, conditionalConfig, conditionalVisibilityConfig, ctaCenteredConfig, ctaClickTrackingConfig, ctaFilledConfig, ctaStandardConfig, darkBackgroundButtonConfig, featuresSectionConfig, headingConfig, headingV2Config, heroColorBlockConfig, heroFeaturesConfig, heroStandardConfig, iconConfig, iconTabsSectionConfig, imgConfig, inViewTrackingConfig, logoBarConfig, logoBarV2Config, oneColumnBlockConfig, planCardSectionConfig, presentationalwrapperConfig, showMoreConfig, richTextConfig, signUpFormConfig, tableSectionConfig, tabsSectionConfig, tagLabelConfig, testimonialMultipleColumnConfig, testimonialSoloConfig, testmonialCarouselConfig, tickerConfig, tooltipWrapperConfig, twoColumnRiverConfig, videoConfig, zapCardSectionConfig, zapierLogoConfig, customCTACenteredConfig, } from './components';
import { withScrollTracking } from './tracking';
import { withMultipleConfigs } from './utils/withMultipleConfigs';
export const coreComponents = [
    accordionConfig,
    accordionSectionConfig,
    anchorConfig,
    articleConfig,
    blockConfig,
    blockV2Config,
    blockGridConfig,
    brandButtonConfig,
    breadcrumbsConfig,
    buttonConfig,
    buttonNavSectionConfig,
    carouselSectionConfig,
    chatBotConfig,
    comparisonTableConfig,
    conditionalConfig,
    conditionalVisibilityConfig,
    ctaCenteredConfig,
    ctaClickTrackingConfig,
    ctaFilledConfig,
    ctaStandardConfig,
    customCTACenteredConfig,
    darkBackgroundButtonConfig,
    featuresSectionConfig,
    headingConfig,
    headingV2Config,
    heroColorBlockConfig,
    heroFeaturesConfig,
    heroStandardConfig,
    iconConfig,
    iconTabsSectionConfig,
    imgConfig,
    inViewTrackingConfig,
    logoBarConfig,
    logoBarV2Config,
    oneColumnBlockConfig,
    planCardSectionConfig,
    presentationalwrapperConfig,
    showMoreConfig,
    richTextConfig,
    signUpFormConfig,
    tableSectionConfig,
    tabsSectionConfig,
    tagLabelConfig,
    testimonialMultipleColumnConfig,
    testimonialSoloConfig,
    testmonialCarouselConfig,
    tickerConfig,
    tooltipWrapperConfig,
    twoColumnRiverConfig,
    videoConfig,
    zapCardSectionConfig,
    zapierLogoConfig,
];
let BuilderIOKitSentry;
export const captureBuilderIOKitException = (e) => {
    if (!BuilderIOKitSentry)
        return;
    BuilderIOKitSentry.withScope(function (scope) {
        if (!scope.setTag || !(BuilderIOKitSentry === null || BuilderIOKitSentry === void 0 ? void 0 : BuilderIOKitSentry.captureException))
            return;
        scope.setTag('builder.io-kit', 'true');
        BuilderIOKitSentry.captureException(e);
    });
};
export function initializeBuilderIOKit(_a) {
    var _b, _c, _d, _e;
    var { apiKey = (_b = process.env.BUILDER_PUBLIC_API_KEY) !== null && _b !== void 0 ? _b : process.env.NEXT_PUBLIC_BUILDER_PUBLIC_API_KEY, customComponents = [], coreComponentsLoader, hasAIPrompts = true, Sentry, } = _a;
    try {
        BuilderIOKitSentry = Sentry;
        if (!apiKey) {
            throw new Error('Builder.io public API key could not be found. The API key can be set with either a `BUILDER_PUBLIC_API_KEY` or `NEXT_PUBLIC_BUILDER_PUBLIC_API_KEY` environment variable or can be manually provided with the `apiKey` option.');
        }
        builder.init(apiKey);
        builder.apiVersion = 'v3';
        const components = customComponents;
        if (coreComponentsLoader) {
            components.push(...coreComponents.map((component) => (Object.assign({ component: coreComponentsLoader(component.name) }, component))));
        }
        /**
         * map of component name to all configs for that component
         * so that we can determine which components have multiple configs by the same name
         */
        const componentMap = components.reduce((acc, component) => {
            if (!acc[component.name])
                acc[component.name] = [];
            acc[component.name].push(component);
            return acc;
        }, {});
        const categories = {};
        // eslint-disable-next-line prefer-const
        for (let _f of components) {
            let { component, category } = _f, config = __rest(_f, ["component", "category"]);
            const configs = componentMap[config.name] || [];
            if ((configs === null || configs === void 0 ? void 0 : configs.length) > 1) {
                // Reversing so that the coreComponet's config is the first one
                component = withMultipleConfigs((_c = componentMap[config.name]) === null || _c === void 0 ? void 0 : _c.reverse());
                if (configs.some((duplicateConfig) => duplicateConfig.canHaveChildren) &&
                    configs.some((duplicateConfig) => !duplicateConfig.canHaveChildren)) {
                    throw new Error(`There are duplicate ${config.name} configs with different values for the \`canHaveChildren\` property. All configs for a component must have the same value for \`canHaveChildren\``);
                }
            }
            if (config.includeScrollTracking) {
                component = withScrollTracking(component);
            }
            component = withBlockContext(component);
            if (hasAIPrompts)
                (_d = config.inputs) === null || _d === void 0 ? void 0 : _d.push({
                    name: 'AIPrompt',
                    friendlyName: 'AI prompt',
                    type: 'AIPrompt',
                    helperText: "Fill out the component's inputs via a prompt to OpenAI",
                });
            // withChildren should be the last HOC before registration or it won't be recognized in registerComponent
            if (config.canHaveChildren) {
                component = withChildren(component);
            }
            Builder.registerComponent(component, config);
            if (category && !config.hideFromInsertMenu) {
                (_e = categories[category]) !== null && _e !== void 0 ? _e : (categories[category] = []);
                categories[category].push({ name: config.name });
            }
        }
        for (const category in categories) {
            Builder.register('insertMenu', {
                name: category,
                items: categories[category],
            });
        }
    }
    catch (error) {
        captureBuilderIOKitException(error);
        throw error;
    }
}
//# sourceMappingURL=init.js.map